import { Button, Typography } from "@mui/material";
import Modal from "../../../App/Components/Modal/Modal";
import {
  useDeleteMessageEmailTemplate,
  useDeleteMessageTextTemplate,
} from "../../../App/Queries/MessageQueries";
import { IEmailTemplate, ITextTemplate } from "../../../App/Typings/MessageText";

interface IDeleteEmailTemplateModalProps {
  deleteTemplate: IEmailTemplate | null;
  setDeleteTemplate: (value: IEmailTemplate | null) => void;
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: (value: boolean) => void;
}

export const DeleteEmailTemplateModal = ({
  deleteTemplate,
  setDeleteTemplate,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
}: IDeleteEmailTemplateModalProps) => {
  const { mutateAsync: deleteEmailTemplate, isLoading: isLoadingDeleteEmailTemplate } =
    useDeleteMessageEmailTemplate();

  const handleCloseModal = () => {
    setIsDeleteDialogOpen(false);
    setTimeout(() => setDeleteTemplate(null), 300);
  };

  const handleDeleteTemplate = async () => {
    if (deleteTemplate) {
      await deleteEmailTemplate(deleteTemplate.emailTemplateId);
      handleCloseModal();
    }
  };

  return (
    deleteTemplate && (
      <Modal
        title="Delete Email Template"
        dataTestId={`delete-email-template${deleteTemplate ? `-${deleteTemplate.emailTemplateId}` : ""}`}
        isOpen={isDeleteDialogOpen}
        handleCloseModal={handleCloseModal}
        content={
          <>
            <Typography variant="h6" sx={{ mb: 1, lineHeight: "1.3" }}>
              Are you sure you want to delete the {deleteTemplate?.isPublic === false ? "private " : ""}email
              template "{deleteTemplate?.name}"?
            </Typography>
            <Typography>This action cannot be undone.</Typography>
            {deleteTemplate?.isPublic === true && (
              <Typography sx={{ mt: 1 }}>
                Deleting this template will remove it from the list of available templates for all users.
              </Typography>
            )}
          </>
        }
        footerContent={
          <Button
            data-test="confirm-delete-template-button"
            onClick={handleDeleteTemplate}
            color="error"
            variant="contained"
            disabled={isLoadingDeleteEmailTemplate}
          >
            Delete
          </Button>
        }
      />
    )
  );
};

interface IDeleteTextTemplateModalProps {
  deleteTemplate: ITextTemplate | null;
  setDeleteTemplate: (value: ITextTemplate | null) => void;
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: (value: boolean) => void;
}

export const DeleteTextTemplateModal = ({
  deleteTemplate,
  setDeleteTemplate,
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
}: IDeleteTextTemplateModalProps) => {
  const { mutateAsync: deleteTextTemplate, isLoading: isLoadingDeleteTextTemplate } =
    useDeleteMessageTextTemplate();

  const handleDeleteTemplate = async () => {
    if (deleteTemplate) {
      await deleteTextTemplate(deleteTemplate.textTemplateId);
      setDeleteTemplate(null);
    }
    setIsDeleteDialogOpen(false);
  };
  return (
    <Modal
      title="Delete Text Template"
      dataTestId={`delete-text-template${deleteTemplate ? `-${deleteTemplate.textTemplateId}` : ""}`}
      isOpen={isDeleteDialogOpen}
      handleCloseModal={() => {
        setIsDeleteDialogOpen(false);
        setTimeout(() => setDeleteTemplate(null), 300);
      }}
      content={
        <>
          <Typography variant="h6" sx={{ mb: 1, lineHeight: "1.3" }}>
            Are you sure you want to delete the {deleteTemplate?.isPublic === false ? "private " : ""}text
            template "{deleteTemplate?.name}"?
          </Typography>
          <Typography>This action cannot be undone.</Typography>
          {deleteTemplate?.isPublic === true && (
            <Typography sx={{ mt: 1 }}>
              Deleting this template will remove it from the list of available templates for all users.
            </Typography>
          )}
        </>
      }
      footerContent={
        <Button
          data-test="confirm-delete-template-button"
          onClick={handleDeleteTemplate}
          color="error"
          variant="contained"
          disabled={isLoadingDeleteTextTemplate}
        >
          Delete
        </Button>
      }
    />
  );
};
